import { fileToBuffer } from './file-to-buffer';
import { loadImg } from './load-img';

export const resizeImage = async (
  src: string,
  size: number,
  fit: 'cover' | 'normal',
  quality = 0.85,
  type = 'image/jpeg'
): Promise<{ src: string; image: HTMLImageElement }> => {
  const image = await loadImg(src);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const aspect = image.naturalHeight / image.naturalWidth;

  size = Math.min(size, Math.max(image.naturalHeight, image.naturalWidth));

  const width = aspect > 1 ? size : size / aspect;
  const height = aspect < 1 ? size : size * aspect;

  if (fit === 'cover') {
    const negativeX = aspect < 1 ? (width - size) / 2 : 0;
    const negativeY = aspect > 1 ? (height - size) / 2 : 0;

    canvas.width = size;
    canvas.height = size;
    ctx.drawImage(image, -negativeX, -negativeY, size + negativeX * 2, size + negativeY * 2);
  } else {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
  }

  return { src: canvas.toDataURL(type, quality), image };
};

export const getAndResizeImageFileAsObjectUrl = async (
  file: File,
  size: number,
  fit: 'cover' | 'normal'
): Promise<{ src: string; image: HTMLImageElement }> => {
  const extension = file.name.split('.').pop().toLowerCase();
  const allowedTypes = ['jpg', 'jpeg', 'gif', 'png'];

  if (!allowedTypes.includes(extension)) {
    throw 'invalid_format';
  }

  const buffer = await fileToBuffer(file);
  const src = URL.createObjectURL(new Blob([buffer]));
  return resizeImage(src, size, fit, undefined, `image/${extension}`);
};

export const getAndResizeImageFile = async (
  file: File,
  size: number,
  fit: 'cover' | 'normal'
): Promise<{ file: File; extension: string }> => {
  const extension = file.name.split('.').pop().toLowerCase();
  // if (['gif', 'webp'].includes(extension) && file.size < maxAnimatedGifSize) return { file, extension };
  const src = await getAndResizeImageFileAsObjectUrl(file, size, fit);
  const blob = await fetch(src.src).then(r => r.blob());
  const resizedFile = new File([blob], `image.${extension}`, { type: `image/${extension}` });
  return { file: resizedFile, extension };
};

export const fitImageIntoAspectRatio = async (
  src: string,
  aspectRatio: number,
  quality = 0.85,
  type = 'image/jpeg'
) => {
  const imgElement = await loadImg(src);

  // Create a canvas element
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Unable to get canvas context');
  }

  // Original image dimensions
  const originalWidth = imgElement.width;
  const originalHeight = imgElement.height;

  // Calculate new dimensions
  let newWidth = originalWidth;
  let newHeight = originalWidth / aspectRatio;

  if (newHeight > originalHeight) {
    newHeight = originalHeight;
    newWidth = originalHeight * aspectRatio;
  }

  // Calculate position to center the crop area
  const startX = (originalWidth - newWidth) / 2;
  const startY = (originalHeight - newHeight) / 2;

  // Set canvas size to the new dimensions
  canvas.width = newWidth;
  canvas.height = newHeight;

  ctx.drawImage(imgElement, startX, startY, newWidth, newHeight, 0, 0, newWidth, newHeight);

  return canvas.toDataURL(type, quality);
};
